import React, { useState } from 'react';
import './MissionDetail.css';
import syncService from '../services/syncService';
import apiService from '../services/apiService';
import cloneManager from '../services/cloneManager';

function MissionDetail({ mission, onBack, onGoHome, onStartInspection, offlineHint = false, detailLoading = false }) {
  const [isLoading, setIsLoading] = useState(false);

  const inspecteurCourant = cloneManager.getInspecteur();
  const inspecteurCourantId = inspecteurCourant?.id || null;
  const rapportLie = mission?.rapport || null;
  const lock = rapportLie?.lock || null;
  const lockedByMe = !!lock && lock.lockedBy === 'pwa' && lock.lockedByInspecteurId === inspecteurCourantId;
  const lockedByOtherPwa = !!lock && lock.lockedBy === 'pwa' && !lockedByMe;
  const lockedByWeb = !!lock && lock.lockedBy === 'web';
  const lockBlocked = lockedByOtherPwa || lockedByWeb;

  const statut = mission.statut_mobile || mission.statut;
  const rawNotes = mission.notes || mission.observations || '';
  const isSystemNotes = /^(Créée automatiquement|Cycle récurrent)/i.test(String(rawNotes).trim());
  const notes = isSystemNotes ? '' : rawNotes;
  const adresse = mission.adresse_complete
    || [mission.site_adresse, [mission.code_postal_etablissement, mission.ville_etablissement].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    || mission.site_adresse
    || '';
  const machines = Array.isArray(mission.machines) ? mission.machines : [];
  const contacts = Array.isArray(mission.client_contacts) ? mission.client_contacts : [];
  const clientPhone = mission.client_telephone
    || contacts.find((c) => c.telephone)?.telephone
    || '';
  const etabContactNom = mission.etablissement_contact_nom || mission.etablissement_contact || '';
  const etabContactPhone = mission.etablissement_contact_telephone || mission.etablissement_telephone || '';

  const getActiviteIcon = (activite) => {
    const icons = {
      electricite: '⚡',
      levage: '🏗️',
      incendie: '🔥',
      thermographie: '🌡️',
    };
    return icons[activite] || '📋';
  };

  const getStatusColor = (status) => {
    const colors = {
      a_venir: '#3b82f6',
      en_cours: '#f59e0b',
      completee: '#10b981',
      annulee: '#6b7280',
      planifiee: '#3b82f6',
      creee: '#f59e0b',
      terminee: '#10b981',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      a_venir: 'À venir',
      en_cours: 'En cours',
      completee: 'Complétée',
      annulee: 'Annulée',
      planifiee: 'Planifiée',
      creee: 'À planifier',
      terminee: 'Terminée',
    };
    return labels[status] || status;
  };

  const getRapportStatutLabel = (s) => {
    const labels = {
      brouillon: 'Brouillon',
      en_cours: 'En cours',
      valide: 'Validé',
      envoye: 'Envoyé',
      facture: 'Facturé',
      termine: 'Terminé',
    };
    return labels[s] || s || '—';
  };

  const tryAcquireLock = async () => {
    if (!rapportLie?.id) {
      return { ok: true, deferred: true };
    }
    const res = await apiService.lockRapport(rapportLie.id, 'pwa');
    if (!res.success) {
      const owner = res.data?.lockedByName ? ` (${res.data.lockedByName})` : '';
      const where = res.data?.lockedBy ? ` sur ${res.data.lockedBy.toUpperCase()}` : '';
      window.alert(`Impossible de démarrer la visite : rapport déjà verrouillé${owner}${where}.\n\nDemandez à la personne concernée de déverrouiller le rapport.`);
      return { ok: false };
    }
    return { ok: true };
  };

  const resolveInspectionType = () => {
    if (mission.activite === 'levage') return 'levage';
    if (mission.activite === 'thermographie') return 'thermographie';
    return 'electricite';
  };

  const handleStartInspection = async () => {
    setIsLoading(true);
    try {
      const lockRes = await tryAcquireLock();
      if (!lockRes.ok) return;

      const result = await syncService.downloadMission(mission.id);
      const inspectionType = resolveInspectionType();

      if (result.success) {
        onStartInspection(mission, inspectionType);
      } else {
        const reason = result.error ? `\n\nRaison: ${result.error}` : '';
        if (window.confirm(`Impossible de télécharger les données du serveur.${reason}\n\nVoulez-vous continuer avec les données locales ?`)) {
          onStartInspection(mission, inspectionType);
        }
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      if (window.confirm('Erreur de connexion.\n\nVoulez-vous continuer hors ligne ?')) {
        onStartInspection(mission, resolveInspectionType());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReleaseLock = async () => {
    if (!rapportLie?.id) return;
    if (!window.confirm('Déverrouiller ce rapport ?\n\nLe bureau pourra alors le modifier depuis l\'app web. Vous pourrez le re-verrouiller plus tard en cliquant sur "Reprendre la visite".')) return;
    setIsLoading(true);
    try {
      const res = await apiService.unlockRapport(rapportLie.id);
      if (!res.success) {
        window.alert(`Impossible de déverrouiller : ${res.error}`);
      } else {
        window.alert('Rapport déverrouillé. Le bureau peut maintenant y accéder.');
        if (typeof onBack === 'function') onBack();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openMaps = () => {
    if (adresse) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresse)}`, '_blank');
    } else {
      alert('Adresse non disponible');
    }
  };

  if (isLoading) {
    return (
      <div className="mission-detail-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '20px',
        minHeight: '60vh',
      }}>
        <div className="mission-detail-spinner" />
        <p style={{ color: '#374151', fontWeight: 600 }}>
          Téléchargement des données...
        </p>
      </div>
    );
  }

  return (
    <div className="mission-detail-container">
      <div className="mission-detail-header">
        <button type="button" className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <h1>Fiche d&apos;intervention</h1>
        {typeof onGoHome === 'function' ? (
          <button type="button" className="home-nav-button" onClick={onGoHome}>
            Accueil
          </button>
        ) : (
          <div />
        )}
      </div>

      <div className="mission-detail-content">
        {offlineHint && (
          <div className="fiche-banner fiche-banner--warn">
            Mode hors ligne / données partielles — certaines infos (machines, contacts) peuvent manquer.
          </div>
        )}
        {detailLoading && (
          <div className="fiche-banner fiche-banner--info">
            Actualisation de la fiche…
          </div>
        )}

        {/* 1. En-tête */}
        <div className="mission-detail-hero">
          <div className="mission-detail-icon">
            {getActiviteIcon(mission.activite)}
          </div>
          <h2 className="mission-detail-numero">
            {mission.rapport?.numero || mission.numero_rapport || `Mission ${mission.activite || ''}`}
          </h2>
          <span
            className="mission-detail-status"
            style={{ backgroundColor: getStatusColor(statut) }}
          >
            {getStatusLabel(statut)}
          </span>
          <div className="fiche-hero-meta">
            <div className="fiche-hero-meta__item">
              {mission.date_affectation || mission.date_prevue
                ? new Date(mission.date_affectation || mission.date_prevue).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Date non définie'}
            </div>
            {(mission.heure_debut || mission.heure_fin) && (
              <div className="fiche-hero-meta__item">
                🕐 {[mission.heure_debut, mission.heure_fin].filter(Boolean).join(' – ')}
                {mission.duree_minutes ? ` · ${mission.duree_minutes} min` : ''}
              </div>
            )}
            {mission.is_recurrent && (
              <div className="fiche-hero-meta__item fiche-chip">
                Récurrente{mission.periode_recurrence ? ` · ${mission.periode_recurrence}` : ''}
              </div>
            )}
          </div>
        </div>

        <div className="mission-detail-sections">
          {/* 2. Lieu */}
          <div className="detail-section">
            <div className="section-icon">📍</div>
            <div className="section-content">
              <div className="section-label">Lieu d&apos;intervention</div>
              <div className="section-value">{mission.client_nom || 'Client'}</div>
              <div className="section-value-secondary">{mission.etablissement || 'Établissement non renseigné'}</div>
              {mission.etablissement_activite && (
                <div className="section-value-secondary">Activité site : {mission.etablissement_activite}</div>
              )}
              <div className="section-value-secondary" style={{ marginTop: 6 }}>
                {adresse || 'Adresse non renseignée'}
              </div>
              {mission.client_siret && (
                <div className="section-value-secondary">SIRET : {mission.client_siret}</div>
              )}
            </div>
          </div>

          {/* 3. Téléphones / contacts */}
          <div className="detail-section">
            <div className="section-icon">📞</div>
            <div className="section-content">
              <div className="section-label">Téléphones</div>
              <div className="fiche-phone-row">
                <span className="fiche-phone-label">Client</span>
                {clientPhone ? (
                  <a className="fiche-link fiche-phone-value" href={`tel:${clientPhone}`}>{clientPhone}</a>
                ) : (
                  <span className="section-value-secondary">Non renseigné</span>
                )}
              </div>
              <div className="fiche-phone-row">
                <span className="fiche-phone-label">Contact établissement</span>
                <div className="fiche-phone-value-block">
                  {etabContactNom && (
                    <div className="section-value" style={{ fontSize: 15 }}>{etabContactNom}</div>
                  )}
                  {etabContactPhone ? (
                    <a className="fiche-link" href={`tel:${etabContactPhone}`}>{etabContactPhone}</a>
                  ) : (
                    <span className="section-value-secondary">
                      {etabContactNom ? 'Téléphone non renseigné' : 'Non renseigné'}
                    </span>
                  )}
                </div>
              </div>
              {mission.client_email && (
                <div className="fiche-phone-row">
                  <span className="fiche-phone-label">Email client</span>
                  <a className="fiche-link" href={`mailto:${mission.client_email}`}>{mission.client_email}</a>
                </div>
              )}
            </div>
          </div>

          {/* 4. Type */}
          <div className="detail-section">
            <div className="section-icon">📋</div>
            <div className="section-content">
              <div className="section-label">Type de vérification</div>
              <div className="section-value">{mission.type_verification || mission.activite || '—'}</div>
              <div className="section-value-secondary">Activité : {mission.activite || '—'}</div>
            </div>
          </div>

          {/* 5. Notes (saisies en planification web) */}
          <div className="detail-section">
            <div className="section-icon">📝</div>
            <div className="section-content">
              <div className="section-label">Notes</div>
              {notes ? (
                <div className="section-value fiche-notes">{notes}</div>
              ) : (
                <div className="section-value-secondary">Aucune note de planification</div>
              )}
            </div>
          </div>

          {/* 6. Machines */}
          {machines.length > 0 && (
            <div className="detail-section">
              <div className="section-icon">🏭</div>
              <div className="section-content">
                <div className="section-label">Parc machines ({machines.length})</div>
                <ul className="fiche-list">
                  {machines.map((m) => (
                    <li key={m.id}>
                      <strong>{m.nom || m.designation || 'Machine'}</strong>
                      {[m.type, m.marque, m.modele].filter(Boolean).length > 0 && (
                        <span> — {[m.type, m.marque, m.modele].filter(Boolean).join(' · ')}</span>
                      )}
                      {m.numero_serie && (
                        <div className="section-value-secondary">N° série : {m.numero_serie}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 7. Rapport */}
          <div className="detail-section">
            <div className="section-icon">📄</div>
            <div className="section-content">
              <div className="section-label">Rapport associé</div>
              {rapportLie ? (
                <>
                  <div className="section-value">
                    {rapportLie.numero || mission.numero_rapport || rapportLie.id.slice(0, 8)}
                  </div>
                  <div className="section-value-secondary">
                    Statut : {getRapportStatutLabel(rapportLie.statut)}
                    {rapportLie.type_rapport ? ` · ${rapportLie.type_rapport}` : ''}
                  </div>
                </>
              ) : (
                <div className="section-value-secondary">
                  Aucun rapport créé pour l&apos;instant — il sera créé au démarrage / sync de la visite.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mission-detail-actions">
          {lockBlocked && (
            <div className={`fiche-lock-banner ${lockedByWeb ? 'is-web' : 'is-pwa'}`}>
              {lockedByWeb && '🔒 Rapport verrouillé côté bureau (web)'}
              {lockedByOtherPwa && `🔒 Verrouillé sur PWA par ${lock?.lockedByName || 'un autre inspecteur'}`}
              <div className="fiche-lock-banner__hint">
                {lockedByWeb
                  ? "Vous ne pouvez pas modifier ce rapport tant que le bureau n'a pas terminé sa finalisation."
                  : "Vous ne pouvez pas modifier ce rapport tant que l'autre inspecteur n'a pas déverrouillé."}
              </div>
            </div>
          )}

          {!lockBlocked && (statut === 'a_venir' || statut === 'planifiee' || statut === 'creee'
            || (!mission.statut_mobile && !mission.statut)) && !lockedByMe && (
            <button type="button" className="action-button primary" onClick={handleStartInspection}>
              ▶️ Démarrer la visite
            </button>
          )}

          {!lockBlocked && lockedByMe && (
            <button type="button" className="action-button warning" onClick={handleStartInspection}>
              ↩️ Reprendre la visite
            </button>
          )}

          {!lockBlocked && !lockedByMe && (statut === 'en_cours') && (
            <button type="button" className="action-button warning" onClick={handleStartInspection}>
              📝 Continuer l&apos;inspection
            </button>
          )}

          {(statut === 'completee' || statut === 'terminee') && (
            <>
              <button type="button" className="action-button success" onClick={handleStartInspection}>
                👁️ Consulter le rapport
              </button>
              {!lockBlocked && (
                <button type="button" className="action-button primary" onClick={handleStartInspection}>
                  ✏️ Modifier le rapport
                </button>
              )}
            </>
          )}

          {lockedByMe && (
            <button
              type="button"
              className="action-button secondary"
              onClick={handleReleaseLock}
              style={{ marginTop: '8px' }}
            >
              🔓 Déverrouiller (rendre au bureau)
            </button>
          )}

          <button type="button" className="action-button secondary" onClick={openMaps}>
            🗺️ Itinéraire
          </button>
        </div>
      </div>
    </div>
  );
}

export default MissionDetail;
